/* eslint-disable import/no-relative-packages */
import { useState, useEffect, useCallback, useRef } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { Hydrate } from "react-query/hydration";
import { ReactQueryDevtools } from "react-query/devtools";
import { find } from "lodash";
import { useRouter } from "next/router";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import {
  useConsentStatus,
  COOKIE_CATEGORIES,
  CONSENT_STATUS,
  usePrevious,
} from "@gonoodle/gn-universe-utils";

import { AppConfigProvider } from "../contexts/appConfig";
import { AnalyticsProvider } from "../contexts/Analytics";
import UserProvider, { useUser } from "../contexts/user";
import SplashScreen from "../components/SplashScreen";
import { USER_TYPES } from "../constants";
import BugsnagClient from "../utils/bugsnag";
import config from "../config";

import "../../../assets/styles/tailwind.css";
import "../../../assets/styles/vars.css";
import "../../../assets/styles/utils.css";
import { ErrorPage } from "./_error";

const ErrorBoundary = BugsnagClient.getPlugin("react").createErrorBoundary();

export default function GoNoodle({ Component, pageProps }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={config.RECAPTCHA_ENTERPRISE_KEY}
      scriptProps={{
        async: true,
        useEnterprise: true,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <AppConfigProvider>
            <UserProvider defaultUser={pageProps.user}>
              <ErrorBoundary FallbackComponent={ErrorPage}>
                <AnalyticsProvider>
                  <BootstrapClientAnalytics trackPage={pageProps.trackPage} />
                  <SplashScreen>
                    <Component {...pageProps} />
                  </SplashScreen>
                </AnalyticsProvider>
              </ErrorBoundary>
            </UserProvider>
          </AppConfigProvider>
        </Hydrate>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </GoogleReCaptchaProvider>
  );
}

const BootstrapClientAnalytics = ({ trackPage }) => {
  const [segmentAnalytics, setSegmentAnalytics] = useState();
  const mounted = useRef(false);
  const router = useRouter();
  const { user = {}, updateUser, isLoading } = useUser();
  const performanceConsentStatus = useConsentStatus(
    COOKIE_CATEGORIES.performanceCookieGroup,
  );
  const prevPerformanceConsentStatus = usePrevious(performanceConsentStatus);

  useEffect(() => {
    mounted.current = true;
    const loadAnalytics = async () => {
      const [instance] = await AnalyticsBrowser.load(
        {
          writeKey: process.env.NEXT_PUBLIC_ANALYTICS_API_KEY,
        },
        { initialPageview: true },
      );

      if (mounted.current) {
        setSegmentAnalytics(instance);
      }
    };

    if (
      !segmentAnalytics &&
      performanceConsentStatus === CONSENT_STATUS.consentGiven
    ) {
      loadAnalytics();
    }

    return () => {
      mounted.current = false;
    };
  }, [performanceConsentStatus, segmentAnalytics]);

  useEffect(() => {
    if (
      prevPerformanceConsentStatus === CONSENT_STATUS.consentGiven &&
      performanceConsentStatus === CONSENT_STATUS.consentNotGiven
    ) {
      segmentAnalytics?.reset();
      window.location.reload();
    }
  }, [
    performanceConsentStatus,
    prevPerformanceConsentStatus,
    segmentAnalytics,
  ]);

  useEffect(() => {
    const handleRouteChange = (_, { shallow }) => {
      if (
        trackPage &&
        performanceConsentStatus === CONSENT_STATUS.consentGiven &&
        shallow === false
      ) {
        segmentAnalytics?.page();
      }
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [performanceConsentStatus, router.events, segmentAnalytics, trackPage]);

  const identify = useCallback(() => {
    segmentAnalytics.identify(user.analyticsId, {
      user_type: find(USER_TYPES, { id: user.userTypeId })?.name,
    });
  }, [segmentAnalytics, user.analyticsId, user.userTypeId]);

  useEffect(() => {
    if (
      isLoading === false &&
      performanceConsentStatus === CONSENT_STATUS.consentGiven
    ) {
      segmentAnalytics?.ready(() => {
        identify();
      });
    }
  }, [isLoading, segmentAnalytics, identify, performanceConsentStatus]);

  useEffect(() => {
    if (
      user.allowTracking === false &&
      performanceConsentStatus === CONSENT_STATUS.consentGiven
    ) {
      updateUser({ allowTracking: true });
    } else if (
      user.allowTracking === true &&
      performanceConsentStatus === CONSENT_STATUS.consentNotGiven
    ) {
      updateUser({ allowTracking: false });
    }
  }, [user.allowTracking, updateUser, performanceConsentStatus]);

  return null;
};
